import { existsSync } from "node:fs";
import { delimiter } from "node:path";
import { spawn, spawnSync } from "child_process";
import { getBinDir, getSettingsPath } from "../config.js";
import { SettingsManager } from "../core/settings-manager.js";

let cachedShellConfig: { shell: string; args: string[] } | null = null;
let cachedPowerShellConfig: PowerShellConfig | null = null;

export interface PowerShellConfig {
	shell: string;
	args: string[];
	flavor: "pwsh" | "powershell";
	/** Whether to hide the window on Windows (windowsHide spawn option) */
	windowsHide: boolean;
}

function findExecutableOnPath(command: string): string | null {
	if (process.platform === "win32") {
		// Windows: Use 'where' and verify file exists (where can return non-existent paths)
		try {
			const result = spawnSync("where", [command], { encoding: "utf-8", timeout: 5000 });
			if (result.status === 0 && result.stdout) {
				const firstMatch = result.stdout.trim().split(/\r?\n/)[0];
				if (firstMatch && existsSync(firstMatch)) {
					return firstMatch;
				}
			}
		} catch {
			// Ignore errors
		}
		return null;
	}

	// Unix: Use 'which' and trust its output (handles Termux and special filesystems)
	try {
		const result = spawnSync("which", [command], { encoding: "utf-8", timeout: 5000 });
		if (result.status === 0 && result.stdout) {
			const firstMatch = result.stdout.trim().split(/\r?\n/)[0];
			if (firstMatch) {
				return firstMatch;
			}
		}
	} catch {
		// Ignore errors
	}
	return null;
}

/**
 * Find bash executable on PATH (cross-platform)
 */
function findBashOnPath(): string | null {
	return findExecutableOnPath(process.platform === "win32" ? "bash.exe" : "bash");
}

/**
 * Get shell configuration based on platform.
 * Resolution order:
 * 1. User-specified shellPath in settings.json
 * 2. On Windows: Git Bash in known locations, then bash on PATH
 * 3. On Unix: /bin/bash, then bash on PATH, then fallback to sh
 */
export function getShellConfig(): { shell: string; args: string[] } {
	if (cachedShellConfig) {
		return cachedShellConfig;
	}

	const settings = SettingsManager.create();
	const customShellPath = settings.getShellPath();

	// 1. Check user-specified shell path
	if (customShellPath) {
		if (existsSync(customShellPath)) {
			cachedShellConfig = { shell: customShellPath, args: ["-c"] };
			return cachedShellConfig;
		}
		throw new Error(
			`Custom shell path not found: ${customShellPath}\nPlease update shellPath in ${getSettingsPath()}`,
		);
	}

	if (process.platform === "win32") {
		// 2. Try Git Bash in known locations
		const paths: string[] = [];
		const programFiles = process.env.ProgramFiles;
		if (programFiles) {
			paths.push(`${programFiles}\\Git\\bin\\bash.exe`);
		}
		const programFilesX86 = process.env["ProgramFiles(x86)"];
		if (programFilesX86) {
			paths.push(`${programFilesX86}\\Git\\bin\\bash.exe`);
		}

		for (const path of paths) {
			if (existsSync(path)) {
				cachedShellConfig = { shell: path, args: ["-c"] };
				return cachedShellConfig;
			}
		}

		// 3. Fallback: search bash.exe on PATH (Cygwin, MSYS2, WSL, etc.)
		const bashOnPath = findBashOnPath();
		if (bashOnPath) {
			cachedShellConfig = { shell: bashOnPath, args: ["-c"] };
			return cachedShellConfig;
		}

		throw new Error(
			`No bash shell found. Options:\n` +
				`  1. Install Git for Windows: https://git-scm.com/download/win\n` +
				`  2. Add your bash to PATH (Cygwin, MSYS2, etc.)\n` +
				`  3. Set shellPath in ${getSettingsPath()}\n\n` +
				`Searched Git Bash in:\n${paths.map((p) => `  ${p}`).join("\n")}`,
		);
	}

	// Unix: try /bin/bash, then bash on PATH, then fallback to sh
	if (existsSync("/bin/bash")) {
		cachedShellConfig = { shell: "/bin/bash", args: ["-c"] };
		return cachedShellConfig;
	}

	const bashOnPath = findBashOnPath();
	if (bashOnPath) {
		cachedShellConfig = { shell: bashOnPath, args: ["-c"] };
		return cachedShellConfig;
	}

	cachedShellConfig = { shell: "sh", args: ["-c"] };
	return cachedShellConfig;
}

export function getPowerShellConfig(): PowerShellConfig {
	if (cachedPowerShellConfig) {
		return cachedPowerShellConfig;
	}

	const args = ["-NoLogo", "-NoProfile", "-NonInteractive", "-Command"];
	const windowsHide = process.platform === "win32";

	// Resolution order:
	// 1. Explicit JENSEN_PWSH_PATH environment variable
	const explicitPath = process.env.JENSEN_PWSH_PATH;
	if (explicitPath) {
		if (existsSync(explicitPath)) {
			cachedPowerShellConfig = { shell: explicitPath, args, flavor: "pwsh", windowsHide };
			return cachedPowerShellConfig;
		}
		throw new Error(`JENSEN_PWSH_PATH is set to "${explicitPath}" but the file does not exist or is not executable.`);
	}

	if (process.platform === "win32") {
		const pwsh = findExecutableOnPath("pwsh.exe");
		if (pwsh) {
			cachedPowerShellConfig = { shell: pwsh, args, flavor: "pwsh", windowsHide };
			return cachedPowerShellConfig;
		}

		const powershell = findExecutableOnPath("powershell.exe");
		if (powershell) {
			cachedPowerShellConfig = { shell: powershell, args, flavor: "powershell", windowsHide };
			return cachedPowerShellConfig;
		}

		throw new Error(
			"PowerShell is not available on this system. Install PowerShell 7 (pwsh) or ensure Windows PowerShell is available on PATH.",
		);
	}

	const pwsh = findExecutableOnPath("pwsh");
	if (pwsh) {
		cachedPowerShellConfig = { shell: pwsh, args, flavor: "pwsh", windowsHide: false };
		return cachedPowerShellConfig;
	}

	throw new Error(
		"PowerShell is not available on this system. On non-Windows hosts, install PowerShell 7+ (pwsh) or use the bash tool instead.",
	);
}

export function resetShellConfigCache(): void {
	cachedShellConfig = null;
	cachedPowerShellConfig = null;
}

export function getShellEnv(): NodeJS.ProcessEnv {
	const binDir = getBinDir();
	const pathKey = Object.keys(process.env).find((key) => key.toLowerCase() === "path") ?? "PATH";
	const currentPath = process.env[pathKey] ?? "";
	const pathEntries = currentPath.split(delimiter).filter(Boolean);
	const hasBinDir = pathEntries.includes(binDir);
	const updatedPath = hasBinDir ? currentPath : [binDir, currentPath].filter(Boolean).join(delimiter);

	return {
		...process.env,
		[pathKey]: updatedPath,
	};
}

/**
 * Sanitize binary output for display/storage.
 * Removes characters that crash string-width or cause display issues:
 * - Control characters (except tab, newline, carriage return)
 * - Lone surrogates
 * - Unicode Format characters (crash string-width due to a bug)
 * - Characters with undefined code points
 */
export function sanitizeBinaryOutput(str: string): string {
	// Use Array.from to properly iterate over code points (not code units)
	// This handles surrogate pairs correctly and catches edge cases where
	// codePointAt() might return undefined
	return Array.from(str)
		.filter((char) => {
			// Filter out characters that cause string-width to crash
			// This includes:
			// - Unicode format characters
			// - Lone surrogates (already filtered by Array.from)
			// - Control chars except \t \n \r
			// - Characters with undefined code points

			const code = char.codePointAt(0);

			// Skip if code point is undefined (edge case with invalid strings)
			if (code === undefined) return false;

			// Allow tab, newline, carriage return
			if (code === 0x09 || code === 0x0a || code === 0x0d) return true;

			// Filter out control characters (0x00-0x1F, except 0x09, 0x0a, 0x0x0d)
			if (code <= 0x1f) return false;

			// Filter out Unicode format characters
			if (code >= 0xfff9 && code <= 0xfffb) return false;

			return true;
		})
		.join("");
}

/**
 * Kill a process and all its children (cross-platform)
 */
export function killProcessTree(pid: number): void {
	if (process.platform === "win32") {
		// Use taskkill on Windows to kill process tree
		try {
			spawn("taskkill", ["/F", "/T", "/PID", String(pid)], {
				stdio: "ignore",
				detached: true,
			});
		} catch {
			// Ignore errors if taskkill fails
		}
	} else {
		// Use SIGKILL on Unix/Linux/Mac
		try {
			process.kill(-pid, "SIGKILL");
		} catch {
			// Fallback to killing just the child if process group kill fails
			try {
				process.kill(pid, "SIGKILL");
			} catch {
				// Process already dead
			}
		}
	}
}

// ============================================================================
// PowerShell remote execution utilities
// ============================================================================

/**
 * PowerShell preamble for remote execution via SSH.
 * Sets up: error action preference, progress suppression, output encoding.
 * Compatible with Windows PowerShell 5.1 and PowerShell 7+.
 */
export const REMOTE_POWERSHELL_PREAMBLE = [
	"$ErrorActionPreference = 'Stop'",
	"$ProgressPreference = 'SilentlyContinue'",
	"[Console]::OutputEncoding = [System.Text.UTF8Encoding]::new()",
].join("; ");

/**
 * Encode a PowerShell script as a UTF-16LE Base64 string suitable for
 * powershell.exe -EncodedCommand.
 *
 * Windows PowerShell and pwsh both require UTF-16LE encoding.
 * Using UTF-8 produces garbled output or parse errors.
 *
 * No BOM is included — both PowerShell 7 and Windows PowerShell 5.1
 * accept UTF-16LE without BOM. A BOM (U+FEFF) causes corruption of
 * the first statement when used with -EncodedCommand.
 *
 * This function is pure — it does not execute anything, does not shell-quote,
 * and is fully unit-testable.
 *
 * @param source - PowerShell source code (can contain Unicode)
 * @returns Base64-encoded UTF-16LE string (no BOM)
 */
export function encodePowerShellCommand(source: string): string {
	const buf = Buffer.alloc(source.length * 2);
	for (let i = 0; i < source.length; i++) {
		buf.writeUInt16LE(source.charCodeAt(i), i * 2);
	}
	return buf.toString("base64");
}

/**
 * Validate an SSH hostname/destination for use in spawn argv.
 *
 * Rejects: empty hosts, hosts starting with "-" (option injection),
 * hosts containing spaces or newlines, hosts that are empty after trim.
 *
 * Accepts: user@host, host-alias, IPv4, IPv6 (with or without brackets),
 * and any hostname valid per RFC 952/1123.
 *
 * Throws a descriptive Error on invalid input so the caller gets a
 * clear failure instead of a silently corrupted command.
 */
export function validateSshHost(host: string): void {
	if (!host || host.trim().length === 0) {
		throw new Error("SSH host must not be empty");
	}
	if (host.startsWith("-")) {
		throw new Error(`SSH host starts with "-" — possible option injection: "${host}"`);
	}
	if (/[\s]/.test(host)) {
		throw new Error(`SSH host contains whitespace — possible injection: "${host}"`);
	}
}

/**
 * Build argv for executing a PowerShell command on a remote Windows host via SSH.
 *
 * Produces a structured argv array suitable for spawn/exec with shell: false.
 * Uses -EncodedCommand for reliable quoting, exit code propagation, and Unicode.
 *
 * The returned array includes "--" to prevent hostname-based option injection.
 *
 * @param host - SSH hostname (validated; must not be empty, start with -, or contain spaces)
 * @param command - PowerShell source code
 * @returns Structured argv for the ssh process
 */
export function buildRemotePowerShellArgs(host: string, command: string): string[] {
	validateSshHost(host);
	const preamble = REMOTE_POWERSHELL_PREAMBLE;
	const fullScript = `${preamble}; ${command}`;
	const encoded = encodePowerShellCommand(fullScript);
	return ["--", host, "powershell.exe", "-NoProfile", "-NonInteractive", "-EncodedCommand", encoded];
}

/**
 * Build argv for a simple PowerShell command on a remote Windows host via SSH.
 *
 * Uses -Command (not -EncodedCommand). Only appropriate for simple commands
 * without pipes, complex quoting, multiline scripts, or untrusted data.
 *
 * The returned array includes "--" to prevent hostname-based option injection.
 *
 * @param host - SSH hostname (validated)
 * @param command - Simple PowerShell command
 * @returns Structured argv for the ssh process
 */
export function buildSimpleRemotePowerShellArgs(host: string, command: string): string[] {
	validateSshHost(host);
	return ["--", host, "powershell.exe", "-NoProfile", "-NonInteractive", "-Command", command];
}
